/**
 * Source unique des statistiques institutionnelles MTB BTP.
 *
 * Règle stricte : aucune statistique n'est affichée si `is_verified` est faux.
 * Toute valeur doit être adossée à un document justificatif interne
 * (`supporting_document`) et datée (`verified_at`).
 *
 * Les anciennes valeurs publiées sans preuve sont conservées ici pour
 * historique (`legacy_value`) mais ne sont jamais rendues côté public.
 */
export interface CompanyStat {
  key:
    | 'projects_delivered'
    | 'years_experience'
    | 'square_meters_built'
    | 'workforce_mobilized'
    | 'satisfaction_rate';
  /** Valeur affichable (string formatée) — null tant que non vérifiée */
  value: string | null;
  label_fr: string;
  label_en: string;
  /** Référence du document de preuve (Drive, attestation, PV de réception…) */
  supporting_document: string | null;
  /** Date de vérification interne (ISO) */
  verified_at: string | null;
  is_verified: boolean;
  /** Ancienne valeur affichée sans preuve — archivée, jamais publiée */
  legacy_value?: string;
}

export const COMPANY_STATS: CompanyStat[] = [
  {
    key: 'projects_delivered',
    value: null,
    label_fr: 'Projets réalisés',
    label_en: 'Projects delivered',
    supporting_document: null,
    verified_at: null,
    is_verified: false,
    legacy_value: '50+',
  },
  {
    key: 'years_experience',
    value: null,
    label_fr: "Années d'expérience",
    label_en: 'Years of experience',
    supporting_document: null,
    verified_at: null,
    is_verified: false,
    legacy_value: '10+',
  },
  {
    key: 'square_meters_built',
    value: null,
    label_fr: 'm² construits',
    label_en: 'm² built',
    supporting_document: null,
    verified_at: null,
    is_verified: false,
    legacy_value: '25 000 / 26 000+',
  },
  {
    key: 'workforce_mobilized',
    value: null,
    label_fr: 'Collaborateurs mobilisés',
    label_en: 'Workforce mobilized',
    supporting_document: null,
    verified_at: null,
    is_verified: false,
    legacy_value: '120+ / 540+',
  },
  {
    key: 'satisfaction_rate',
    value: null,
    label_fr: 'Taux de satisfaction',
    label_en: 'Satisfaction rate',
    supporting_document: null,
    verified_at: null,
    is_verified: false,
    legacy_value: '98%',
  },
];

/** Statistiques publiables : vérifiées ET pourvues d'une valeur + preuve. */
export const getVerifiedStats = (): CompanyStat[] =>
  COMPANY_STATS.filter(
    (s) => s.is_verified && !!s.value && !!s.supporting_document,
  );

export const hasVerifiedStats = (): boolean => getVerifiedStats().length > 0;

/** Bloc de remplacement institutionnel affiché tant qu'aucune stat n'est vérifiée. */
export const CAPABILITIES_FALLBACK = {
  fr: {
    title: 'Nos capacités opérationnelles',
    text: 'MTB BTP mobilise des compétences techniques, administratives et opérationnelles adaptées à chaque projet. Les références détaillées sont communiquées après validation et selon les obligations de confidentialité.',
  },
  en: {
    title: 'Our operational capabilities',
    text: 'MTB BTP mobilises technical, administrative and operational expertise tailored to each project. Detailed references are shared after validation and in line with confidentiality obligations.',
  },
} as const;
